import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiSave, FiX } from 'react-icons/fi';
import { supabase, TABLES } from '../../supabase/supabase';

const JourneyMapConfig = ({ projectId, existingModule, onSave, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    instructions: '',
    lanes: {
      top: 'Exceeded expectations',
      middle: 'Neutral',
      bottom: 'Below expectations'
    }
  });

  useEffect(() => {
    if (existingModule) {
      setFormData({
        name: existingModule.name || '',
        instructions: existingModule.config?.instructions || '',
        lanes: {
          top: existingModule.config?.lanes?.find(l => l.position === 'top')?.label || 'Exceeded expectations',
          middle: existingModule.config?.lanes?.find(l => l.position === 'middle')?.label || 'Neutral',
          bottom: existingModule.config?.lanes?.find(l => l.position === 'bottom')?.label || 'Below expectations'
        }
      });
    }
  }, [existingModule]);

  const handleSave = async () => {
    if (!formData.name) return;
    setLoading(true);
    try {
      const config = {
        instructions: formData.instructions,
        lanes: [
          { position: 'top', label: formData.lanes.top },
          { position: 'middle', label: formData.lanes.middle },
          { position: 'bottom', label: formData.lanes.bottom }
        ]
      };

      const { data: moduleData, error: mError } = await supabase
        .from(TABLES.MODULES)
        .upsert({
          id: existingModule?.id,
          project_id: projectId,
          name: formData.name,
          type: 'journey_map',
          config
        })
        .select()
        .single();

      if (mError) throw mError;

      // Sync lanes table
      await supabase.from(TABLES.JOURNEY_MAP_LANES).delete().eq('module_id', moduleData.id);
      
      const lanesPayload = [
        { module_id: moduleData.id, name: formData.lanes.top, label: formData.lanes.top, y_position: 1.0, order_index: 0, type: 'standard' },
        { module_id: moduleData.id, name: formData.lanes.middle, label: formData.lanes.middle, y_position: 0.5, order_index: 1, type: 'standard' },
        { module_id: moduleData.id, name: formData.lanes.bottom, label: formData.lanes.bottom, y_position: 0.0, order_index: 2, type: 'standard' }
      ];

      const { error: lError } = await supabase.from(TABLES.JOURNEY_MAP_LANES).insert(lanesPayload);
      if (lError) throw lError;

      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md p-8 shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="space-y-6">
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Module name</label>
            <input 
              type="text" 
              className="rutabaga-input" 
              placeholder="e.g., Your Fitness Journey"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1 flex items-center gap-2">
              Participant instructions
              <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[8px] font-black text-gray-400 uppercase">Optional</span>
            </label>
            <textarea 
              className="rutabaga-input min-h-[100px] resize-none" 
              placeholder="Describe the start and end point of the journey..."
              value={formData.instructions}
              onChange={e => setFormData({ ...formData, instructions: e.target.value })}
            />
          </div>

          <div className="space-y-4">
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Lane Labels</h4>
            {['top', 'middle', 'bottom'].map((pos) => (
              <div key={pos}>
                <label className="block text-[8px] font-black text-gray-400 uppercase mb-1 ml-1">{pos} lane</label>
                <input 
                  type="text" 
                  className="rutabaga-input py-2 text-sm"
                  value={formData.lanes[pos]}
                  onChange={e => setFormData({ 
                    ...formData, 
                    lanes: { ...formData.lanes, [pos]: e.target.value } 
                  })}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Preview</h4>
          <div className="flex-1 border border-gray-100 rounded-lg overflow-hidden flex flex-col bg-gray-50 p-2 gap-1">
            <div className="flex-1 bg-emerald-50/50 border border-emerald-100/30 rounded flex items-center justify-center p-4">
              <span className="text-[10px] font-black text-emerald-600 uppercase text-center">{formData.lanes.top || 'Top Lane'}</span>
            </div>
            <div className="flex-1 bg-gray-100/50 border border-gray-200/30 rounded flex items-center justify-center p-4">
              <span className="text-[10px] font-black text-gray-400 uppercase text-center">{formData.lanes.middle || 'Middle Lane'}</span>
            </div>
            <div className="flex-1 bg-amber-50/50 border border-amber-100/30 rounded flex items-center justify-center p-4">
              <span className="text-[10px] font-black text-amber-600 uppercase text-center">{formData.lanes.bottom || 'Bottom Lane'}</span>
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <button 
              onClick={onCancel}
              className="px-6 py-3 text-gray-400 font-bold hover:text-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              disabled={loading || !formData.name}
              className="flex-1 rutabaga-button-primary flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiSave} />
              {loading ? 'Saving...' : 'Save Journey Map'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyMapConfig;